import React, { useEffect, useState } from 'react';
import { View, Text, ActivityIndicator } from 'react-native';

import { supabase } from '../../lib/supabase';
import { authService } from '../services/authService';
import { redactUrl } from '../utils/redactUrl';

interface OAuthCallbackProps {
  onSuccess: () => void;
  onError: (error: string) => void;
}

const OAuthCallback: React.FC<OAuthCallbackProps> = ({ onSuccess, onError }) => {
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const [message, setMessage] = useState('Processing authentication...');

  useEffect(() => {
    // Check if we're in a mobile browser (not the React Native app)
    const isMobileBrowser = typeof window !== 'undefined' &&
                           typeof navigator !== 'undefined' &&
                           /Android|iPhone|iPad|iPod/i.test(navigator.userAgent) &&
                           !Object.prototype.hasOwnProperty.call(window, 'ReactNativeWebView');

    if (isMobileBrowser) {
      console.log('📱 [OAuth Callback] Mobile browser detected - redirecting to app');
      setMessage('Redirecting to app...');

      // Build deep link URL with all OAuth parameters
      const urlParams = new URLSearchParams(window.location.search);
      const hashParams = new URLSearchParams(window.location.hash.substring(1));

      let deepLinkUrl = 'kingdombiblequiz://auth/callback';
      const params = new URLSearchParams();

      // Copy all parameters
      for (const [key, value] of urlParams.entries()) {
        params.append(key, value);
      }
      for (const [key, value] of hashParams.entries()) {
        params.append(key, value);
      }

      if (params.toString()) {
        deepLinkUrl += '?' + params.toString();
      }

      console.log('📱 [OAuth Callback] Redirecting to:', redactUrl(deepLinkUrl));

      // Try to open the app
      setTimeout(() => {
        window.location.href = deepLinkUrl;

        // Update UI to show manual link if auto-redirect fails
        setTimeout(() => {
          setMessage('Tap below to return to the app');
          setStatus('success');
        }, 2000);
      }, 500);

      return; // Don't process OAuth in browser, let the app handle it
    }

    // Set a timeout fallback in case OAuth processing hangs
    const timeoutId = setTimeout(() => {
      if (status === 'processing') {
        console.warn('⚠️ [OAuth Callback] Timeout - checking if user is already signed in');
        supabase.auth.getSession().then(({ data: { session } }) => {
          if (session?.user) {
            console.log('✅ [OAuth Callback] Found existing session after timeout, completing auth');
            setStatus('success');
            setMessage('Authentication successful!');
            setTimeout(() => onSuccess(), 500);
          } else {
            console.error('❌ [OAuth Callback] Timeout with no session');
            setStatus('error');
            setMessage('Authentication timed out. Please try again.');
            setTimeout(() => onError('Authentication timed out'), 2000);
          }
        });
      }
    }, 10000); // 10 second timeout

    handleOAuthCallback();

    return () => clearTimeout(timeoutId);
  }, []);

  const handleOAuthCallback = async () => {
    try {
      console.log('🔄 [OAuth Callback] Processing OAuth callback...');
      
      // Check if we're on web and have URL parameters
      if (typeof window !== 'undefined') {
        const urlParams = new URLSearchParams(window.location.search);
        const code = urlParams.get('code');
        const error = urlParams.get('error');
        const errorDescription = urlParams.get('error_description');
        
        console.log('🔍 [OAuth Callback] URL parameters:', {
          code: code ? 'present' : 'missing',
          error,
          errorDescription
        });
        
        if (error) {
          console.error('❌ [OAuth Callback] OAuth error from URL:', error, errorDescription);
          setStatus('error');
          setMessage(errorDescription || error);
          onError(errorDescription || error);
          return;
        }

        // Note: code parameter might not be present if Supabase already exchanged it
        // This is normal - we'll check for session below
        if (!code) {
          console.log('⚠️ [OAuth Callback] No code in URL - checking for existing session');
        }
      }
      
      // First, try to exchange the auth code if present
      if (typeof window !== 'undefined') {
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        const accessToken = hashParams.get('access_token');
        const refreshToken = hashParams.get('refresh_token');

        if (accessToken) {
          console.log('🔄 [OAuth Callback] Found tokens in hash, setting session...');
          const { error: setSessionError } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken || ''
          });

          if (setSessionError) {
            console.error('❌ [OAuth Callback] Failed to set session:', setSessionError);
          }
        }
      }

      // Get the current session
      console.log('🔄 [OAuth Callback] Getting session...');
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError) {
        console.error('❌ [OAuth Callback] Session error:', sessionError);
        setStatus('error');
        setMessage('Failed to establish session');
        onError(sessionError.message);
        return;
      }
      
      if (!session?.user) {
        console.error('❌ [OAuth Callback] No session or user found');
        setStatus('error');
        setMessage('Authentication failed - no user session');
        onError('Authentication failed - no user session');
        return;
      }
      
      console.log('✅ [OAuth Callback] Session established');
      
      // Create or update user profile
      setMessage('Setting up your profile...');
      const user = await authService.createOrUpdateOAuthProfile(session.user);
      
      console.log('✅ [OAuth Callback] Profile setup complete');
      setStatus('success');
      setMessage('Authentication successful!');
      
      // Clean up URL
      if (typeof window !== 'undefined') {
        window.history.replaceState({}, document.title, window.location.pathname);
      }
      
      // Notify success
      setTimeout(() => {
        onSuccess();
      }, 1000);
      
    } catch (error: any) {
      console.error('❌ [OAuth Callback] Callback processing failed:', error);
      setStatus('error');
      setMessage(error.message || 'Authentication failed');
      onError(error.message || 'Authentication failed');
    }
  };

  const getStatusColor = () => {
    switch (status) {
      case 'processing': return '#3B82F6';
      case 'success': return '#10B981';
      case 'error': return '#EF4444';
      default: return '#3B82F6';
    }
  };

  const getStatusIcon = () => {
    switch (status) {
      case 'processing': return '🔄';
      case 'success': return '✅';
      case 'error': return '❌';
      default: return '🔄';
    }
  };

  // Get deep link URL for manual redirect
  const getDeepLinkUrl = () => {
    if (typeof window === 'undefined') return 'kingdombiblequiz://';

    const urlParams = new URLSearchParams(window.location.search);
    const hashParams = new URLSearchParams(window.location.hash.substring(1));

    let deepLinkUrl = 'kingdombiblequiz://auth/callback';
    const params = new URLSearchParams();

    for (const [key, value] of urlParams.entries()) {
      params.append(key, value);
    }
    for (const [key, value] of hashParams.entries()) {
      params.append(key, value);
    }

    if (params.toString()) {
      deepLinkUrl += '?' + params.toString();
    }

    return deepLinkUrl;
  };

  return (
    <View style={{
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
      backgroundColor: '#1F2937',
      padding: 20
    }}>
      <View style={{
        backgroundColor: 'rgba(255, 255, 255, 0.1)',
        borderRadius: 16,
        padding: 32,
        alignItems: 'center',
        maxWidth: 400,
        width: '100%'
      }}>
        <Text style={{
          fontSize: 48,
          marginBottom: 16
        }}>
          {getStatusIcon()}
        </Text>

        {status === 'processing' && (
          <ActivityIndicator
            size="large"
            color={getStatusColor()}
            style={{ marginBottom: 16 }}
          />
        )}

        <Text style={{
          fontSize: 18,
          fontWeight: '600',
          color: '#FFFFFF',
          textAlign: 'center',
          marginBottom: 8
        }}>
          {status === 'processing' && 'Completing Sign In...'}
          {status === 'success' && 'Welcome to Kingdom Bible Quiz!'}
          {status === 'error' && 'Authentication Failed'}
        </Text>

        <Text style={{
          fontSize: 14,
          color: 'rgba(255, 255, 255, 0.8)',
          textAlign: 'center',
          lineHeight: 20
        }}>
          {message}
        </Text>

        {status === 'success' && typeof window !== 'undefined' && /Android|iPhone|iPad|iPod/i.test(navigator.userAgent) && (
          <View style={{ marginTop: 24, alignItems: 'center' }}>
            <Text style={{
              fontSize: 12,
              color: 'rgba(255, 255, 255, 0.6)',
              textAlign: 'center',
              marginBottom: 12
            }}>
              If the app doesn't open automatically:
            </Text>
            <a
              href={getDeepLinkUrl()}
              style={{
                display: 'inline-block',
                padding: '12px 24px',
                backgroundColor: '#6200EE',
                color: 'white',
                textDecoration: 'none',
                borderRadius: '8px',
                fontWeight: '600',
                fontSize: '14px'
              }}
            >
              Open Kingdom Bible Quiz
            </a>
          </View>
        )}
      </View>
    </View>
  );
};

export default OAuthCallback;
